import React from "react";
import { cn } from "../../lib/cn";

export type CardProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Rendered at the trailing edge of the header. */
  actions?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  /** Removes body padding, for cards whose body is a full-bleed table or list. */
  flush?: boolean;
};

export function Card({
  title,
  description,
  actions,
  children,
  footer,
  className,
  flush = false,
}: CardProps) {
  return (
    <section
      className={cn(
        "bg-surface border-line shadow-panel overflow-hidden rounded-xl border",
        className,
      )}
    >
      {(title || actions) && (
        <header
          className={cn(
            "flex items-start justify-between gap-3 px-4 py-3",
            // No divider when the body is flush: the table's own header row
            // already reads as the boundary.
            !flush && "border-line border-b",
          )}
        >
          <div className="min-w-0">
            {title && (
              <h2 className="text-ink text-sm leading-tight font-semibold">
                {title}
              </h2>
            )}
            {description && (
              <p className="text-ink-muted mt-1 text-xs leading-relaxed">
                {description}
              </p>
            )}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
        </header>
      )}

      {children && <div className={cn(!flush && "px-4 py-4")}>{children}</div>}

      {footer && (
        <footer className="bg-surface-sunken border-line flex items-center justify-end gap-2 border-t px-4 py-3">
          {footer}
        </footer>
      )}
    </section>
  );
}

export default Card;
